import { BRAND_KEY } from '../molecules/ColorPicker/utils'
import { DONATION_DEFAULT_TITLE, getEmptyTextWithAmount } from './DonationForm/donationUtils'
import { PROGRESS_BAR_DEFAULT_HTML_TEXT, PROGRESS_BAR_DEFAULT_TITLE } from './ProgressBar/progressBarUtils'
import { Column, CommentsLayout, ComponentType, Footer, Header, PageComponent, Row } from '@web/graphql-components'

export enum ComponentLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export const DOLLAR_HANDLES_DEFAULT_AMOUNTS = [25, 75, 100, 125, 150, 175]

export const BASE_STYLES = {
  width: '100%',
  margin: '10px 0px 10px 0px',
}

export const DEFAULT_BACKGROUND_COLOR = {
  backgroundColor: '#FFFFFF',
  backgroundColorOpacity: '0',
}

const POST_DONATION_FORM_TITLE =
  '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"A few more details","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}'

export const createPostDonationFormTitle = (id = 'post-donation-form-title') => ({
  id,
  type: ComponentType.Text,
  textValue: POST_DONATION_FORM_TITLE,
  htmlText: '',
  styles: BASE_STYLES,
  mobileStyles: BASE_STYLES,
})

export const DEFAULT_POST_DONATION_FORM = {
  id: 'post-donation-form',
  type: ComponentType.PostDonationForm,
  buttonText: 'Submit',
  extraFields: [],
  isFresh: true,
  styles: {
    backgroundColor: '#E2E8F0',
    backgroundColorOpacity: '1',
    borderRadius: 15,
    padding: '24px 24px 24px 24px',
    useDefaultBranding: true,
    ...BASE_STYLES,
  },
  mobileStyles: {
    width: '100%',
    padding: '20px 20px 20px 20px',
  },
}

export const DEFAULT_DONATION_FORM = {
  id: 'donation-form',
  type: ComponentType.DonationForm,
  title: DONATION_DEFAULT_TITLE,
  donationFrequency: [],
  isFresh: true,
  dollarHandles: [
    { amount: 25, description: '', imageUrl: '' },
    { amount: 75, description: '', imageUrl: '' },
    { amount: 100, description: '', imageUrl: '' },
  ],
  styles: {
    backgroundColor: '#E2E8F0',
    backgroundColorOpacity: '1',
    borderColor: '#FFFFFF',
    borderColorOpacity: '0',
    borderRadius: 15,
    padding: '40px 40px 40px 40px',
    useDefaultBranding: true,
    ...BASE_STYLES,
  },
  mobileStyles: {
    width: '100%',
    padding: '20px 20px 20px 20px',
  },
  extraFields: [],
}

export const DEFAULT_TEXT = {
  id: 'text',
  type: ComponentType.Text,
  htmlText: '',
  textValue: `{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add a heading","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"heading","version":1,"tag":"h1"},{"children":[],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add a subheading","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"heading","version":1,"tag":"h2"},{"children":[],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add body text","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}`,
  styles: BASE_STYLES,
  mobileStyles: BASE_STYLES,
}

export const DEFAULT_IMAGE = {
  id: 'image',
  type: ComponentType.Image,
  url: '',
  alt: '',
  isFresh: true,
  styles: BASE_STYLES,
  mobileStyles: BASE_STYLES,
}

const DEFAULT_VIDEO = {
  id: 'video',
  type: ComponentType.Video,
  url: '',
  title: '',
  isFresh: true,
  styles: {
    ...BASE_STYLES,
    aspectRatio: ComponentLayout.Horizontal,
  },
  mobileStyles: {
    ...BASE_STYLES,
    aspectRatio: ComponentLayout.Horizontal,
  },
}

const DEFAULT_DOLLAR_HANDLES = {
  id: 'dollar-handles',
  type: ComponentType.DollarHandles,
  dollarHandles: [
    {
      amount: DOLLAR_HANDLES_DEFAULT_AMOUNTS[0],
      description: getEmptyTextWithAmount(1, 'DollarHandles'),
      imageUrl: '',
      hasImage: true,
      hasDescription: true,
    },
    {
      amount: DOLLAR_HANDLES_DEFAULT_AMOUNTS[1],
      description: getEmptyTextWithAmount(2, 'DollarHandles'),
      imageUrl: '',
      hasImage: true,
      hasDescription: true,
    },
    {
      amount: DOLLAR_HANDLES_DEFAULT_AMOUNTS[2],
      description: getEmptyTextWithAmount(3, 'DollarHandles'),
      imageUrl: '',
      hasImage: true,
      hasDescription: true,
    },
  ],
  styles: {
    ...BASE_STYLES,
    layout: 'vertical',
    backgroundColor: '#FFFFFF',
    backgroundColorOpacity: '1',
    borderColor: '#E2E8F0',
    borderColorOpacity: '1',
    borderRadius: 4,
    isLinkedWithDonationForm: true,
  },
  mobileStyles: BASE_STYLES,
  isFresh: true,
}

const DEFAULT_COMMENTS = {
  id: 'comments',
  type: ComponentType.Comments,
  showAmount: true,
  showMessage: true,
  showTotal: false,
  showViewAll: false,
  layout: CommentsLayout.Horizontal,
  styles: {
    backgroundColor: BRAND_KEY,
    backgroundColorOpacity: '1',
    messageBackgroundColor: '#FFFFFF',
    messageBackgroundColorOpacity: '1',
    messageBorderColor: '#E2E8F0',
    messageBorderColorOpacity: '1',
    ...BASE_STYLES,
    padding: '20px 0px 20px 0px',
  },
  mobileStyles: { width: '100%', padding: '20px 0px 20px 0px' },
}

export const DEFAULT_PROGRESS_BAR = {
  id: 'progress-bar',
  type: ComponentType.ProgressBar,
  goals: [0],
  title: PROGRESS_BAR_DEFAULT_TITLE,
  text: {
    textValue: PROGRESS_BAR_DEFAULT_TITLE,
    htmlText: PROGRESS_BAR_DEFAULT_HTML_TEXT,
  },
  hideOnEnd: false,
  hideBeforeStart: false,
  hideOnGoalReached: false,
  styles: {
    ...BASE_STYLES,
    padding: '20px 20px 20px 20px',
    backgroundColor: '#E2E8F0',
    backgroundColorOpacity: '1',
    barColor: '#FFFFFF',
    barColorOpacity: '1',
    progressColor: BRAND_KEY,
    progressColorOpacity: '1',
  },
  mobileStyles: { ...BASE_STYLES, padding: '20px 20px 20px 20px' },
}

export const DEFAULT_PROGRESS_IMAGE = {
  id: 'progress-image',
  type: ComponentType.ProgressImage,
  styles: BASE_STYLES,
  mobileStyles: BASE_STYLES,
  subComponents: {
    image: {
      id: 'progress-image-image',
      type: ComponentType.Image,
      url: '',
      alt: '',
      styles: {},
      mobileStyles: {},
    },
  },
}

const DEFAULT_COUNTDOWN_TIMER = {
  id: 'countdown-timer',
  type: ComponentType.CountdownTimer,
  hideOnEnd: false,
  hideBeforeStart: false,
  styles: {
    ...BASE_STYLES,
    padding: '20px 20px 20px 20px',
    backgroundColor: BRAND_KEY,
    backgroundColorOpacity: '1',
    labelColor: '#FFFFFF',
    labelColorOpacity: '1',
    timerColor: '#FFFFFF',
    timerColorOpacity: '1',
    borderRadius: 15,
  },
  mobileStyles: { ...BASE_STYLES, padding: '15px 15px 15px 15px' },
}

const DEFAULT_BUTTON = {
  id: 'button',
  type: ComponentType.Button,
  buttonText: 'Donate',
  url: '#donation-form-element',
  target: '_self',
  buttonType: 'Primary',
  styles: { padding: '8px 16px 8px 16px', ...BASE_STYLES },
  mobileStyles: { padding: '8px 16px 8px 16px', ...BASE_STYLES },
}

export const DEFAULT_SOCIAL_SHARE = {
  id: 'social-share',
  type: ComponentType.SocialShare,
  hasLink: true,
  hasFacebook: true,
  hasTwitter: true,
  hasLinkedIn: true,
  hasWhatsApp: true,
  hasEmail: true,
  styles: { padding: '8px 16px 8px 16px', ...BASE_STYLES },
  mobileStyles: { padding: '8px 16px 8px 16px', ...BASE_STYLES },
}

export const DEFAULT_COUNT_UP = {
  ...DEFAULT_TEXT,
  styles: { ...DEFAULT_TEXT.styles, options: { mergeTags: { 'Page.AmountRaised': true } } },
  id: 'count-up',
  textValue:
    '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"We\'ve raised $","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Amount Raised","MergeFieldSettingId":"Page.AmountRaised","MergeTag":"{{Page.AmountRaised}}","MergeTagFallback":"0","MergeTagType":"Super"},"format":0,"styles":{},"fallback":"0"}],"direction":"ltr","format":"center","indent":0,"type":"heading","version":1,"tag":"h1"}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
  htmlText: `<h1 class="c-editor-text__title" dir="ltr" style="text-align: center;"><span style="white-space: pre-wrap;">We've raised $</span><span data-merge-tag="true" data-merge-tag-value="Page.AmountRaised" data-merge-tag-fallback="0" data-merge-tag-type="Super" class="" style="">0</span></h1>`,
  label: 'Count Up',
}

export const DEFAULT_FUNDRAISER_BUTTON = {
  id: 'fundraiser-button',
  type: ComponentType.FundraiserButton,
  value: 'login',
  loginForm: {
    id: '',
    type: ComponentType.LoginForm,
    title:
      '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"Fundraiser Login","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
  },
  registerForm: {
    id: '',
    type: ComponentType.RegisterForm,
    title:
      '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"Register To Fundraise","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
  },
  styles: BASE_STYLES,
}

export const DEFAULT_LOGIN_FORM = {
  id: 'login-form',
  type: ComponentType.LoginForm,
  styles: {
    ...DEFAULT_DONATION_FORM.styles,
    hasTitle: true,
    textColor: '#000000',
    backgroundColor: '#E6F0F8',
    borderColor: 'transparent',
  },
  mobileStyles: DEFAULT_DONATION_FORM.mobileStyles,
  subComponents: {
    title: {
      id: 'login-form-title',
      type: ComponentType.Text,
      textValue:
        '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"Fundraiser Login","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
      htmlText: '',
      styles: BASE_STYLES,
      mobileStyles: BASE_STYLES,
    },
  },
}

export const DEFAULT_REGISTER_FORM = {
  id: 'register-form',
  type: ComponentType.RegisterForm,
  isPeerToPeerGivingNotificationEnabled: false,
  styles: {
    ...DEFAULT_DONATION_FORM.styles,
    hasTitle: true,
    textColor: '#000000',
    backgroundColor: '#E6F0F8',
    borderColor: '#FFFFFF',
  },
  mobileStyles: DEFAULT_DONATION_FORM.mobileStyles,
  subComponents: {
    title: {
      id: 'register-form-title',
      type: ComponentType.Text,
      textValue:
        '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"","text":"Register To Fundraise","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
      htmlText: '',
      styles: BASE_STYLES,
      mobileStyles: BASE_STYLES,
    },
  },
}

export const DEFAULT_FUNDRAISER_PROFILE = {
  id: 'fundraiser-profile',
  type: ComponentType.FundraiserProfile,
  styles: {
    ...BASE_STYLES,
    backgroundColor: '#FFFFFF',
    backgroundColorOpacity: '0',
    borderRadius: 4,
    hasProfileImage: true,
    hasSocialShare: true,
    profileImageShape: 'full',
  },
  mobileStyles: { ...BASE_STYLES, hasProfileImage: true, hasSocialShare: true },
  subComponents: {
    profileName: {
      id: 'profile-text',
      type: ComponentType.Text,
      textValue:
        '{"root":{"children":[{"children":[{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Profile Name","MergeFieldSettingId":"Profile.Name","MergeTag":"{{Profile.Name}}"},"format":1,"styles":{"font-family":null,"font-size":null}},{"detail":0,"format":0,"mode":"normal","style":"","text":"","type":"text","version":1}],"direction":null,"format":"","indent":0,"type":"heading","version":1,"tag":"h2"}],"direction":null,"format":"","indent":0,"type":"root","version":1}}',
      htmlText:
        '<h2 class="c-editor-text__section-title"><span data-merge-tag="true" data-merge-tag-value="Profile.Name" data-merge-tag-fallback="" data-merge-tag-type="" class="c-editor-text--bold" style="">{{Profile.Name}}</span><span style="white-space: pre-wrap;"></span></h2>',
      styles: {
        ...BASE_STYLES,
        margin: '16px 0px 16px 0px',
        backgroundColor: '#FFFFFF',
        backgroundColorOpacity: '0',
        borderRadius: 4,
      },
      mobileStyles: BASE_STYLES,
    },
    socialShare: {
      id: 'social-share',
      type: ComponentType.SocialShare,
      hasLink: true,
      hasFacebook: true,
      hasTwitter: true,
      hasLinkedIn: true,
      hasWhatsApp: true,
      hasEmail: true,
      styles: { ...BASE_STYLES, margin: '0px 0px 16px 0px' },
      mobileStyles: BASE_STYLES,
    },
  },
}

export const DEFAULT_LEADERBOARD: EditorComponent = {
  id: 'leaderboard',
  type: ComponentType.Leaderboard,
  styles: {
    ...BASE_STYLES,
    backgroundColor: '#FFFFFF',
    backgroundColorOpacity: '0',
    borderColor: '#FFFFFF',
    borderColorOpacity: '0',
    borderRadius: 0,
    arrowColor: '#000000',
    hasTitle: true,
    hasSearchBar: false,
    profile: {
      backgroundColor: '#FFFFFF',
      backgroundColorOpacity: '0',
      textColor: '#000000',
      borderColor: '#FFFFFF',
      borderColorOpacity: '0',
      borderRadius: 4,
      hasProfileImage: true,
      hasRanking: true,
      hasMedalColors: false,
      rankingColor: BRAND_KEY,
      rankingTextColor: '#FFFFFF',
      hasProgressBar: true,
      progressBarColor: BRAND_KEY,
      progressBarBackgroundColor: '#F4F4F5',
      hasAmountRaised: true,
    },
  },
  mobileStyles: BASE_STYLES,
  subComponents: {
    title: {
      id: 'leaderboard-title',
      type: ComponentType.Text,
      textValue:
        '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Meet Our Champions","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
      htmlText:
        '<h1 class="c-editor-text__title" dir="ltr" style="text-align: center;"><span style="white-space: pre-wrap;">Meet Our Champions</span></h1>',
      styles: BASE_STYLES,
      mobileStyles: BASE_STYLES,
    },
  },
}

export type EditorComponent = PageComponent & { isFresh?: boolean }

const BASE_COMPONENTS: EditorComponent[] = [DEFAULT_TEXT, DEFAULT_IMAGE, DEFAULT_VIDEO, DEFAULT_BUTTON]

export const PAGE_COMPONENTS: EditorComponent[] = [
  ...BASE_COMPONENTS,
  DEFAULT_DOLLAR_HANDLES,
  DEFAULT_COMMENTS,
  DEFAULT_COUNTDOWN_TIMER,
  DEFAULT_SOCIAL_SHARE,
]

export const THANK_YOU_COMPONENTS: EditorComponent[] = [
  ...BASE_COMPONENTS,
  DEFAULT_SOCIAL_SHARE,
  DEFAULT_POST_DONATION_FORM,
]
export const LOGIN_COMPONENTS: EditorComponent[] = [DEFAULT_LOGIN_FORM, ...PAGE_COMPONENTS]
export const REGISTER_COMPONENTS: EditorComponent[] = [DEFAULT_REGISTER_FORM, ...PAGE_COMPONENTS]
export const PROFILE_COMPONENTS: EditorComponent[] = [
  DEFAULT_FUNDRAISER_PROFILE,
  DEFAULT_LEADERBOARD,
  ...PAGE_COMPONENTS,
]
export const RECEIPT_COMPONENTS: EditorComponent[] = [DEFAULT_TEXT, DEFAULT_IMAGE]

export const MOBILE_PAGE_COMPONENTS: EditorComponent[] = [
  {
    id: 'text',
    type: ComponentType.Text,
    textValue: `{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add a heading","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"heading","version":1,"tag":"h1"},{"children":[],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add a subheading","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"heading","version":1,"tag":"h2"},{"children":[],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"Add body text","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}`,
    styles: { display: 'none' },
    mobileStyles: BASE_STYLES,
  },
  {
    id: 'image',
    type: ComponentType.Image,
    url: '',
    alt: '',
    styles: { display: 'none' },
    mobileStyles: BASE_STYLES,
    isFresh: true,
  },
  {
    id: 'video',
    type: ComponentType.Video,
    url: '',
    title: '',
    styles: { display: 'none' },
    mobileStyles: BASE_STYLES,
    isFresh: true,
  },
  {
    id: 'button',
    type: ComponentType.Button,
    buttonText: 'Button',
    url: '#donation-form-element',
    target: '_self',
    buttonType: 'Primary',
    styles: { display: 'none' },
    mobileStyles: { padding: '8px 16px 8px 16px', ...BASE_STYLES },
  },
]

export const DEFAULT_ROW: Row = {
  id: '',
  type: ComponentType.Row,
  styles: {
    ...BASE_STYLES,
    margin: '0px 0px 0px 0px',
    padding: '40px 20px 40px 20px',
    alignItems: 'start',
    justifyContent: 'start',
  },
  mobileStyles: { width: '100%', padding: '20px 20px 20px 20px', alignItems: 'center', justifyContent: 'center' },
  components: [],
}

export const DEFAULT_COLUMN: Column = {
  id: 'column',
  type: ComponentType.Column,
  components: [],
  styles: {
    ...BASE_STYLES,
    margin: '0px 0px 0px 0px',
    padding: '15px 20px 15px 20px',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'start',
  },
}

export const DEFAULT_FOOTER: Footer = {
  id: 'footer',
  type: ComponentType.Footer,
  taxDeductibleText:
    '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"All donations are tax-deductible.","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
  keyInformationText:
    '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"At ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Name","MergeFieldSettingId":"CharityName","MergeTag":"{{Charity.Name}}"},"format":0,"styles":{},"fallback":""},{"detail":0,"format":0,"mode":"normal","style":"","text":" (ABN: ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity ABN","MergeFieldSettingId":"CharityABN","MergeTag":"{{Charity.ABN}}"},"format":0,"styles":{}},{"detail":0,"format":0,"mode":"normal","style":"","text":"), we use the GiveEasy Platform so your donations are secure.","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"If you have any questions, please get in touch via ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Phone","MergeFieldSettingId":"CharityPhone","MergeTag":"{{Charity.Phone}}"},"format":0,"styles":{},"fallback":""},{"detail":0,"format":0,"mode":"normal","style":"","text":" or via email ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Email","MergeFieldSettingId":"CharityEmail","MergeTag":"{{Charity.Email}}"},"format":0,"styles":{}},{"detail":0,"format":0,"mode":"normal","style":"","text":".","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
  keyInformation: {
    id: '',
    type: ComponentType.Text,
    textValue:
      '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"At ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Name","MergeFieldSettingId":"CharityName","MergeTag":"{{Charity.Name}}"},"format":0,"styles":{},"fallback":""},{"detail":0,"format":0,"mode":"normal","style":"","text":" (ABN: ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity ABN","MergeFieldSettingId":"CharityABN","MergeTag":"{{Charity.ABN}}"},"format":0,"styles":{}},{"detail":0,"format":0,"mode":"normal","style":"","text":"), we use the GiveEasy Platform so your donations are secure.","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1},{"children":[{"detail":0,"format":0,"mode":"normal","style":"","text":"If you have any questions, please get in touch via ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Phone","MergeFieldSettingId":"CharityPhone","MergeTag":"{{Charity.Phone}}"},"format":0,"styles":{},"fallback":""},{"detail":0,"format":0,"mode":"normal","style":"","text":" or via email ","type":"text","version":1},{"type":"merge-tag","version":1,"value":{"QueryParameter":"","CsvHeader":"","TemplateName":"","MergeFieldName":"Charity Email","MergeFieldSettingId":"CharityEmail","MergeTag":"{{Charity.Email}}"},"format":0,"styles":{}},{"detail":0,"format":0,"mode":"normal","style":"","text":".","type":"text","version":1}],"direction":"ltr","format":"","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}',
    htmlText:
      '<p class="c-editor-text__body-text" dir="ltr"><span style="white-space: pre-wrap;">At </span><span data-merge-tag="true" data-merge-tag-value="CharityName" data-merge-tag-fallback="" data-merge-tag-type="" class="" style="">{{Charity.Name}}</span><span style="white-space: pre-wrap;"> (ABN: </span><span data-merge-tag="true" data-merge-tag-value="CharityABN" data-merge-tag-fallback="" data-merge-tag-type="" class="" style="">{{Charity.ABN}}</span><span style="white-space: pre-wrap;">), we use the GiveEasy Platform so your donations are secure.</span></p><p class="c-editor-text__body-text" dir="ltr"><span style="white-space: pre-wrap;">If you have any questions, please get in touch via </span><span data-merge-tag="true" data-merge-tag-value="CharityPhone" data-merge-tag-fallback="" data-merge-tag-type="" class="" style="">{{Charity.Phone}}</span><span style="white-space: pre-wrap;"> or via email </span><span data-merge-tag="true" data-merge-tag-value="CharityEmail" data-merge-tag-fallback="" data-merge-tag-type="" class="" style="">{{Charity.Email}}</span><span style="white-space: pre-wrap;">.</span></p>',
  },
  styles: {},
}

export const DEFAULT_HEADER: Header = {
  id: 'header',
  type: ComponentType.Header,
  hasDonateButton: true,
  styles: DEFAULT_BACKGROUND_COLOR,
}

export const EMPTY_TEXT =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}'
